import React, { useState, useEffect } from "react";
import { updateTradeComment, updateDaySummary } from "../../firebase";
import { HiPencil, HiX, HiOutlineTrash, HiOutlineChatAlt, HiRefresh } from "react-icons/hi";
import { Select } from "flowbite-react";

const DayDetails = ({ details, day, month, year, userId, onRefresh }) => {
  const [localDetails, setLocalDetails] = useState(details);
  const [comments, setComments] = useState({});
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [summary, setSummary] = useState(details.summary || { pl: 0, trades: 0 });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState("All");
  const [instruments, setInstruments] = useState(["All"]);

  useEffect(() => {
    setLocalDetails(details);
    const initialComments = {};
    const uniqueInstruments = new Set(["All"]);
    Object.entries(details.transactions).forEach(([id, transaction]) => {
      initialComments[id] = transaction.comment || "";
      if (transaction.instrumentName) {
        uniqueInstruments.add(transaction.instrumentName);
      }
    });
    setComments(initialComments);
    setSummary(details.summary || { pl: 0, trades: 0 });
    setInstruments(Array.from(uniqueInstruments));
  }, [details]);

  const calculateSummary = (transactions, instrument = "All") => {
    const tradeTransactions = Object.values(transactions).filter(
      (transaction) => transaction.type === "TRADE" && 
        (instrument === "All" || transaction.instrumentName === instrument)
    );
    const pl = tradeTransactions.reduce(
      (sum, transaction) => sum + parseFloat(transaction.amount),
      0
    );
    const trades = tradeTransactions.length;
    return { pl, trades };
  };

  useEffect(() => {
    const newSummary = calculateSummary(localDetails.transactions, selectedInstrument);
    setSummary(newSummary);
  }, [selectedInstrument, localDetails]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const newSummary = calculateSummary(localDetails.transactions);
      await updateDaySummary(userId, year, month, day, newSummary);
      setSummary(newSummary);
      setLocalDetails(prevDetails => ({
        ...prevDetails,
        summary: newSummary
      }));
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Error updating summary:", error);
      alert("Failed to update summary. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCommentChange = (id, value) => {
    setComments({ ...comments, [id]: value });
  };

  const handleCommentSubmit = async (id) => {
    try {
      await updateTradeComment(userId, year, month, day, id, comments[id]);

      setLocalDetails((prevDetails) => ({
        ...prevDetails,
        transactions: {
          ...prevDetails.transactions,
          [id]: {
            ...prevDetails.transactions[id],
            comment: comments[id],
          },
        },
      }));

      setEditingCommentId(null);
    } catch (error) {
      console.error("Error saving comment:", error);
      alert("Failed to save comment. Please try again.");
    }
  };

  const handleDeleteComment = async (id) => {
    try {
      await updateTradeComment(userId, year, month, day, id, "");
  
      setLocalDetails((prevDetails) => ({
        ...prevDetails,
        transactions: {
          ...prevDetails.transactions,
          [id]: {
            ...prevDetails.transactions[id],
            comment: "",
          },
        },
      }));
  
      setComments((prevComments) => ({
        ...prevComments,
        [id]: "",
      }));
  
      setEditingCommentId(null);
    } catch (error) {
      console.error("Error deleting comment:", error);
      alert("Failed to delete comment. Please try again.");
    }
  };

  if (!localDetails || !localDetails.transactions) return null;

  const filteredTransactions = Object.entries(localDetails.transactions).filter(([_, transaction]) => 
    transaction.type === "TRADE" && 
    (selectedInstrument === "All" || transaction.instrumentName === selectedInstrument)
  );

  const otherTransactions = Object.entries(localDetails.transactions).filter(([_, transaction]) => transaction.type !== "TRADE");

  return (
    <div className="p-4 mb-4 rounded-lg dark:bg-slate-800 bg-white text-slate-400">
      <h3 className="text-xl font-semibold">
        Day Summary: {new Date(year, month, day).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
      </h3>
      <div className="flex items-center space-x-2 mb-4">
        <p className={summary.pl >= 0 ? "text-green-500" : "text-red-500"}>Total P/L: £{summary.pl.toFixed(2)}</p>
        <button
          onClick={handleRefresh}
          className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
          title="Refresh summary"
          disabled={isRefreshing}
        >
          <HiRefresh className={`w-5 h-5 text-blue-500 ${isRefreshing ? "animate-spin" : ""}`} />
        </button>
      </div>
      <p className="mb-4">Number of Trades: {summary.trades}</p>
  
      <div className="mb-4">
        <label htmlFor="instrumentFilter" className="block mb-2">
          Filter by Instrument:
        </label>
        <Select id="instrumentFilter" value={selectedInstrument} onChange={(e) => setSelectedInstrument(e.target.value)}>
          {instruments.map((instrument) => (
            <option key={instrument} value={instrument}>
              {instrument}
            </option>
          ))}
        </Select>
      </div>
  
      <h4 className="text-lg mt-4 mb-2">Transactions</h4>
      {filteredTransactions.map(([id, transaction]) => {
        const isProfit = parseFloat(transaction.amount) >= 0;
        const accountTypeColor =
          transaction.accountType === "T212"
            ? "bg-sky-500 bg-opacity-30"
            : transaction.accountType === "CAPITAL" ||
              transaction.accountType === "Capital" ||
              transaction.accountType === "Spread betting"
            ? "bg-yellow-700 bg-opacity-30"
            : "";
  
        return (
          <div key={id} className={`mb-4 p-2 rounded-lg ${accountTypeColor} text-slate-600 dark:text-slate-200`}>
            <div className="flex justify-between items-center">
              <a
                href={`https://www.tradingview.com/chart/?symbol=${transaction.instrumentName}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <h3>{transaction.instrumentName}</h3>
              </a>
              {editingCommentId === id ? (
                <button onClick={() => setEditingCommentId(null)} className="text-blue-500 hover:text-blue-600">
                  <HiX size={20} />
                </button>
              ) : (
                <button onClick={() => setEditingCommentId(id)} className="text-blue-500 hover:text-blue-600">
                  <HiPencil size={20} />
                </button>
              )}
            </div>
  
            <p className={isProfit ? "text-green-500" : "text-red-500"}>
              Amount: {parseFloat(transaction.amount).toFixed(2)} {transaction.currency}
            </p>
            <p>Account: {transaction.accountType}</p>
            <p>Time: {new Date(transaction.modified).toLocaleString()}</p>
  
            {transaction.comment && editingCommentId !== id && (
              <div className="flex flex-row mt-2">
                <HiOutlineChatAlt className="text-blue-500" />
                <p className="ml-2 italic text-blue-500">{transaction.comment}</p>
              </div>
            )}
  
            {editingCommentId === id && (
              <form
                className="flex items-center mt-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCommentSubmit(id);
                }}
              >
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="Add a comment..."
                    value={comments[id] || ""}
                    onChange={(e) => handleCommentChange(id, e.target.value)}
                    className="pe-10 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 end-0 flex items-center pe-3"
                    onClick={() => handleDeleteComment(id)}
                  >
                    <HiOutlineTrash className="w-4 h-4 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" />
                  </button>
                </div>
                <button
                  type="submit"
                  className="inline-flex items-center py-2.5 px-3 ms-2 text-sm font-medium text-white bg-blue-700 rounded-lg border border-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                >
                  Save
                </button>
              </form>
            )}
          </div>
        );
      })}
  
      <h4 className="text-lg mt-4 mb-2">Other Transactions</h4>
      {otherTransactions.map(([id, transaction]) => (
        <div key={id} className="mb-4 p-2 rounded border text-slate-600 dark:text-slate-200">
          {transaction.instrumentName && <p>Instrument: {transaction.instrumentName}</p>}
          <p className={parseFloat(transaction.amount) >= 0 ? "text-green-500" : "text-red-500"}>
            Amount: {parseFloat(transaction.amount).toFixed(2)} {transaction.currency}
          </p>
          <p>Type: {transaction.type}</p>
          <p>Status: {transaction.status}</p>
          <p>Time: {new Date(transaction.modified).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
};

export default DayDetails;